/**
 * Plan Features Configuration
 * Maps subscription plans to accessible modules
 */

export const PLAN_FEATURES = {
  Starter: {
    // Drug-shop tier — sell-from-stock workflow only. POS rides on 'billing'
    // (the /app/pos route and header button are billing-gated); Help & Support
    // is ungated and always available.
    modules: [
      'pharmacy',      // Pharmacy (restricted tabs — see STARTER_TABS)
      'billing',       // Billing + POS
      'finance',       // Accounts (restricted tabs)
      'inventory',     // Inventory (restricted tabs)
      'admin',         // Administration (restricted sections)
    ],
  },
  'Starter 2': {
    // Diagnostic-centre tier — same restricted shell as Starter but swaps
    // pharmacy/POS for diagnostics: patients + laboratory + radiology.
    // POS is explicitly out — the billing POS tab/button is suppressed
    // page-side via isStarter2Plan.
    modules: [
      'patients',      // Patients (no Analytics tab — see STARTER2_HIDDEN_TABS)
      'laboratory',    // Laboratory
      'radiology',     // Radiology
      'billing',       // Billing — payments & returns only (no POS)
      'finance',       // Accounts (restricted tabs)
      'inventory',     // Inventory (restricted tabs)
      'admin',         // Administration (restricted sections)
    ],
  },
  Premium: {
    modules: [
      'patients',      // Patients & Appointments
      'clinical',      // Clinical / EMR
      'laboratory',    // Laboratory
      'radiology',     // Radiology
      'pharmacy',      // Pharmacy
      'billing',       // Billing
      'finance',       // Accounts / Finance
      'inventory',     // Inventory Management
      'admin',         // Administration
      'notifications', // System Notifications & Alerts
    ],
  },
  Enterprise: {
    modules: [
      // All modules included
      'patients',
      'clinical',
      'emergency',
      'ipd',
      'outpatient_therapy',
      'chronic_care',
      'maternal',
      'imnci',
      'laboratory',
      'radiology',
      'procedures',
      'pharmacy',
      'billing',
      'finance',
      'reports',
      'family_planning',
      'blood_bank',
      'insurance',
      'inventory',
      'workforce',
      'chat',
      'analytics',
      'admin',
      'print',
      'online_bookings',
      'notifications',
    ],
  },
};

/**
 * Module metadata - maps routes to module identifiers
 */
export const MODULE_MAP = {
  '/app/patients': 'patients',
  '/app/clinical': 'clinical',
  '/app/emergency': 'emergency',
  '/app/ipd': 'ipd',
  '/app/care': 'ipd',
  '/app/maternal': 'maternal',
  '/app/imnci': 'maternal',
  '/app/family-planning': 'family_planning',
  '/app/laboratory': 'laboratory',
  '/app/radiology': 'radiology',
  '/app/procedures': 'procedures',
  '/app/pharmacy': 'pharmacy',
  '/app/billing': 'billing',
  '/app/finance': 'finance',
  '/app/reports': 'reports',
  '/app/inventory': 'inventory',
  '/app/purchasing': 'inventory',  // Purchasing merged into Inventory
  '/app/admin': 'admin',
  '/app/administration': 'admin',  // Alias for admin module
  '/app/print': 'print',
  '/app/blood-bank': 'blood_bank',
  '/app/insurance': 'insurance',
  '/app/outpatient-therapy': 'outpatient_therapy',
  '/app/chronic-care': 'chronic_care',
  '/app/workforce': 'workforce',
  '/app/chats': 'chat',
  '/app/analytics': 'analytics',
};

/**
 * Premium add-ons that require explicit subscription
 * For Premium plans, these modules are only accessible if the add-on is active
 */
export const PREMIUM_ADDONS = [
  'emergency',
  'ipd',
  'outpatient_therapy',
  'chronic_care',
  'maternal',
  'family_planning',
  'imnci',
  'blood_bank',
  'procedures',
  'insurance',
  'chat',
  'workforce',
  'analytics',
];

/**
 * Enterprise module add-ons — the 12 modules the superadmin can toggle
 * per-tenant on the Enterprise plan. Excludes 'chat' (a locked add-on,
 * always included). When an Enterprise subscription carries an explicit
 * non-empty addOns list, these become a grant subset; an empty/absent
 * list means legacy Enterprise — everything included.
 * Must match ENTERPRISE_MODULE_ADDON_IDS in backend utils/plan_features.py.
 */
export const ENTERPRISE_MODULE_ADDONS = [
  'emergency',
  'ipd',
  'outpatient_therapy',
  'chronic_care',
  'maternal',
  'family_planning',
  'imnci',
  'blood_bank',
  'procedures',
  'insurance',
  'workforce',
  'analytics',
];

/**
 * Standard add-ons — modules NOT in the Premium base plan that a Premium
 * tenant can purchase separately (stored in subscription.extra_metadata.addOns).
 * For Premium plans, these modules are only accessible if the add-on is
 * active. Enterprise includes them in the base plan.
 *
 * NOTE: 'reports' (Printing) is Enterprise-only — NOT sellable on Premium.
 * It stays in PREMIUM_LOCKED_ADDONS (always included on Enterprise).
 *
 * NOTE: 'quick_reports' and 'online_bookings' are Premium add-ons that do
 * not correspond to standalone navigation modules — they unlock sub-sections
 * inside Administration and Patients respectively. 'online_bookings' uses a
 * pseudo-module ID so canAccessModule() can gate the Patients > Bookings tab.
 */
export const STANDARD_ADDONS = [
  'chat',
  'quick_reports',
  'online_bookings',
];

/**
 * Maps a Premium add-on ID to the module(s) it unlocks in the frontend.
 * Most add-ons unlock a single module with the same ID.
 * Add-ons like 'quick_reports' that unlock a sub-section (not a module)
 * map to an empty array.
 *
 * NOTE: the "Printing" add-on (ID 'reports') used to unlock both the
 * reports and print modules. Print Centre is Premium-only for now (not
 * ready to ship on Standard) — re-add 'print' below when it ships.
 */
export const ADDON_MODULES = {
  reports: ['reports'],
  chat: ['chat'],
  quick_reports: [],
  online_bookings: ['online_bookings'],
};

/**
 * Enterprise "locked add-ons" — these Premium add-ons are always included in
 * the Enterprise plan (can't be toggled off). They are auto-granted to
 * Enterprise tenants without depending on subscription metadata.
 */
export const PREMIUM_LOCKED_ADDONS = ['reports', 'chat', 'quick_reports', 'online_bookings'];

/**
 * Tabs locked for Premium plan users (visible with PREMIUM badge, not clickable)
 * Maps module IDs to arrays of tab IDs that are locked on the Premium plan.
 * Enterprise users have full access.
 *
 * Backend enforcement is now in place via require_feature_dependency() in
 * utils/plan_features.py — the corresponding Feature enums are checked
 * server-side on each gated endpoint.
 */
/**
 * Starter plan (drug shops) — allowed tabs per module.
 * Every tab NOT listed here is hidden entirely (no PREMIUM badge, no upsell):
 * the plan ships a deliberately minimal UI. Modules absent from this map have
 * no tab-level restrictions.
 *
 * Sub-view restrictions that can't be expressed as tab ids are handled in the
 * pages themselves (see isStarterPlan): the InventoryIntelligence sub-views,
 * the FinanceReports sub-reports, the New Requisition button, and the
 * Administration sections/sub-tabs below.
 */
export const STARTER_TABS = {
  pharmacy: ['drugs', 'dispensed', 'returns'],
  inventory: ['reorder-alerts', 'receive-goods', 'stock-counts', 'abc-analysis', 'inventory-intelligence'],
  finance: ['expenses', 'reports', 'accounts'],
  billing: ['payments', 'returns'],
};

/**
 * Starter 2 (diagnostic centres) — same restricted shell for the shared
 * modules (inventory/finance/billing/admin), minus billing POS. Patients,
 * laboratory, and radiology keep Premium-style locked badges except the
 * explicit hides in STARTER2_HIDDEN_TABS.
 */
export const STARTER2_TABS = {
  inventory: ['reorder-alerts', 'receive-goods', 'stock-counts', 'abc-analysis', 'inventory-intelligence'],
  finance: ['expenses', 'reports', 'accounts'],
  billing: ['payments', 'returns'],
};

/**
 * Individual tabs hidden on Starter 2 inside otherwise-unrestricted modules.
 * The user asked for Patients "without Analytics tab".
 */
export const STARTER2_HIDDEN_TABS = {
  patients: ['analytics'],
};

/**
 * Add-ons sellable on the entry tiers (matches Pricing.js). Granting works
 * the same way as Premium add-ons: the purchased id lands in
 * subscription.extra_metadata.addOns and ADDON_MODULES maps it to the
 * module(s) it unlocks.
 * - Starter:  Quick Reports only (director/owner's scheduled report)
 * - Starter 2: Quick Reports + Online Bookings (patient portal requests)
 */
export const STARTER_SELLABLE_ADDONS = ['quick_reports'];
export const STARTER2_SELLABLE_ADDONS = ['quick_reports', 'online_bookings'];

/** Administration sidebar sections visible on the Starter plan. */
export const STARTER_ADMIN_SECTIONS = ['users', 'organization', 'localization', 'data'];

/**
 * Sub-tab allowlists inside Administration sections (Starter).
 * Sections not listed here keep all of their sub-tabs.
 */
export const STARTER_ADMIN_SUBTABS = {
  organization: ['profile', 'finance', 'id-formats'],
};

/** InventoryIntelligence sub-views visible on Starter (FSN + Profit only). */
export const STARTER_INTEL_VIEWS = ['fsn', 'profit'];

/** FinanceReports sub-reports visible on Starter (P&L + General Ledger only). */
export const STARTER_FINANCE_REPORTS = ['profit-loss', 'general-ledger'];

/**
 * True when the active subscription is the Starter plan (drug-shop tier).
 * Pages use this for sub-view/button restrictions that aren't expressible as
 * tab ids. Mirrors the plan normalization used inside isTabLocked.
 */
const normalizedPlanName = (subscription) => (
  subscription && subscription.plan && subscription.status === 'active'
    ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1).toLowerCase()
    : null
);

export const isStarterPlan = (subscription) => normalizedPlanName(subscription) === 'Starter';
export const isStarter2Plan = (subscription) => normalizedPlanName(subscription) === 'Starter 2';

/**
 * True for either entry tier (Starter / Starter 2). Pages whose restricted
 * shell is identical across both plans (inventory, finance, billing,
 * administration, header chrome) should branch on this rather than
 * isStarterPlan alone.
 */
export const isEntryTierPlan = (subscription) => isStarterPlan(subscription) || isStarter2Plan(subscription);

/**
 * Data Management (Administration → Export/Import) — plan correspondence.
 * Maps each exportable table to the plan module(s) whose content it holds.
 * A table is shown when ANY owner module is accessible to the tenant (plan
 * base module or purchased add-on, incl. Enterprise's locked add-ons).
 * Tables not listed here are shared platform/organization data shown on
 * every plan (audit anchors, demo bookkeeping, tenant subscription rows).
 */
export const DATA_TABLE_MODULES = {
  patients: [
    'patients', 'patient_contacts', 'patient_documents',
    'patient_family_links', 'patient_next_of_kin', 'patient_referrals',
    'patient_tags',
    'appointments', 'appointment_reminders', 'appointment_slots',
    'patient_insurance',           // + insurance
    'patient_deposits',            // + billing
    'patient_allergies',           // + clinical
    'patient_chronic_conditions',  // + chronic_care
    'patient_booking_requests',    // + online_bookings
    'portal_accounts',             // + online_bookings
    'visits',                      // + clinical
    'referral_centres',            // + clinical
    'incoming_patients',           // + emergency (ED intake board)
  ],
  clinical: [
    'diagnoses', 'clinical_notes', 'triage',       // triage + emergency
    'vital_signs', 'vital_signs_records',           // + ipd + emergency
    'immunizations',                                // + maternal + imnci
    'prescriptions', 'prescription_items',
    'prescription_dispenses',                       // + pharmacy
    'medical_history', 'family_history', 'social_history',
    'allergies', 'referrals', 'followups', 'visit_followups',
    'order_sets', 'soap_note_templates', 'clinical_templates',
    'clinical_macros', 'chief_complaint_catalog',
    'signs_symptoms_catalog', 'diagnosis_catalog',
    'clinical_parameters', 'clinical_protocols',
    'clinical_decision_rules',
    'visit_dispositions',                           // + ipd
    'procedure_orders',                             // + procedures
    'diagnostic_sessions',                          // + laboratory + radiology
    // CDSS configuration — premium-gated in the admin UI; kept under
    // clinical (nearest owned domain) since no dedicated module id exists.
    'cdss_complaints', 'cdss_condition_weights', 'cdss_conditions',
    'cdss_guideline_versions', 'cdss_investigation_suggestions',
    'cdss_question_options', 'cdss_question_sections', 'cdss_questions',
    'cdss_sessions',
  ],
  laboratory: [
    'lab_tests', 'lab_parameters', 'lab_kits', 'lab_test_kits',
    'lab_test_kit_batches', 'lab_test_panels', 'lab_test_panel_items',
    'lab_orders', 'lab_samples', 'lab_results', 'lab_test_results',
    'lab_kit_stock_alerts', 'qc_control_lots', 'qc_runs',
    'diagnostic_sessions',
    'temperature_logs',                             // + blood_bank
  ],
  radiology: [
    'imaging_modalities', 'imaging_studies', 'imaging_reports',
    'dicom_studies', 'pacs_servers', 'contrast_media',
    'contrast_administrations', 'radiology_consumables',
    'radiology_resources', 'radiology_schedules',
    'prior_study_comparisons',
    'diagnostic_sessions',
  ],
  pharmacy: [
    'drugs', 'drug_stock_batches', 'drug_interactions', 'drug_dosages',
    'pharmacy_stock', 'dispense_records', 'dispense_items',
    'dispensing_history', 'prescription_dispenses',
    'emergency_medications',                        // + emergency
    'ipd_medication_administrations',               // + ipd
    'opd_medication_administrations',               // + outpatient_therapy
    'therapy_plan_items',                           // + outpatient_therapy
    'drug_consumable_templates',                    // + inventory + procedures
    'return_cases', 'return_items',                 // + billing
    'stock_alerts', 'stock_count_items', 'stock_transfer_items', // + inventory
  ],
  inventory: [
    'stock_items', 'stock_batches', 'stock_movements',
    'stock_adjustments', 'stock_count_items', 'stock_counts',
    'stock_transfer_items', 'stock_transfers', 'stock_alerts',
    'purchase_orders', 'purchase_order_items',
    'purchase_requisitions', 'purchase_requisition_items',
    'goods_receipt_notes', 'goods_receipt_note_items',
    'suppliers', 'warehouses',
    'drug_consumable_templates',
    'equipment_maintenance', 'equipment_malfunctions',
    'consumable_usage',                             // + procedures
  ],
  billing: [
    'invoices', 'invoice_items', 'payments', 'payment_plans',
    'payment_plan_invoices', 'plan_installments',
    'credit_notes', 'credit_note_items',
    'billing_accounts', 'deposit_usages', 'savings_deposits',
    'service_catalog', 'pos_drafts', 'pos_partial_orders',
    'pos_partial_order_items',
    'patient_deposits', 'return_cases', 'return_items',
  ],
  finance: [
    'expenses', 'expense_categories', 'journal_entries', 'accounts',
    'transactions', 'accounts_receivable', 'accounts_payable',
    'financial_alert_logs', 'budgets',
  ],
  admin: [
    'users', 'login_sessions', 'audit_logs', 'staff_invitations',
    'user_public_keys', 'organization_settings', 'catalog_overrides',
    'departments', 'custom_fields', 'custom_forms',
  ],
  notifications: ['notifications', 'feedback'],
  chat: ['chat_rooms', 'chat_messages', 'chat_room_members'],
  online_bookings: ['patient_booking_requests', 'portal_accounts'],
  quick_reports: [
    'quick_report_subscriptions', 'quick_report_delivery_logs',
    'report_distributions',
  ],
  emergency: [
    'emergency_visits', 'ed_bed_assignments', 'ed_beds', 'ed_rooms',
    'emergency_fluid_balance', 'emergency_medications', 'triage',
    'vital_signs', 'vital_signs_records', 'incoming_patients',
  ],
  ipd: [
    'admissions', 'admission_requests', 'discharge_summaries',
    'bed_transfers', 'wards', 'beds', 'care_plans', 'care_plan_goals',
    'ipd_prognosis', 'ipd_progress_notes', 'ipd_fluid_balance',
    'ipd_medication_administrations', 'nursing_handovers',
    'wound_care_records', 'post_op_care_records',    // + procedures
    'ed_bed_assignments', 'visit_dispositions',
    'vital_signs', 'vital_signs_records',
    'transfusion_records',                          // + blood_bank
  ],
  outpatient_therapy: [
    'therapy_sessions', 'outpatient_therapy_plans', 'therapy_plan_items',
    'opd_fluid_balance', 'opd_medication_administrations',
    'opd_progress_notes', 'opd_requests', 'opd_vital_signs',
  ],
  chronic_care: [
    'chronic_care_enrollments', 'chronic_care_programs',
    'chronic_care_goals', 'chronic_care_medications',
    'chronic_care_refill_requests', 'chronic_care_visits',
    'chronic_care_alerts', 'chronic_care_dispositions',
    'chronic_care_enrollment_queue', 'patient_chronic_conditions',
  ],
  maternal: [
    'pregnancies', 'anc_visits', 'postnatal_visits', 'delivery_records',
    'children', 'maternal_screening_alerts', 'labour_records',
    'developmental_alerts', 'developmental_milestones',
    'milestone_checklist_items', 'growth_monitoring',
    'vitamin_a_supplementation', 'immunizations',
  ],
  family_planning: [
    'fp_clients', 'fp_daily_registers', 'fp_commodities',
    'fp_eligibility_screenings', 'fp_follow_ups',
    'fp_method_administrations',
  ],
  imnci: [
    'imnci_encounters', 'imnci_follow_up_tasks', 'imnci_referral_notes',
    'imnci_assessment_domains', 'imnci_domain_assessments',
    'imnci_dosage_tables', 'imnci_treatment_orders',
    'developmental_alerts', 'developmental_milestones',
    'milestone_checklist_items', 'growth_monitoring',
    'vitamin_a_supplementation', 'deworming',
    'early_intervention_enrollments', 'immunizations',
    'children',
  ],
  blood_bank: [
    'blood_components', 'blood_donations', 'blood_donors',
    'blood_inventory_alerts', 'blood_requests', 'blood_storage_units',
    'crossmatch_tests', 'donor_campaigns', 'transfusion_records',
    'temperature_logs',
  ],
  procedures: [
    'procedure_catalog', 'procedures_catalog', 'procedure_orders',
    'procedure_follow_ups', 'procedure_media', 'procedure_templates',
    'procedure_supply_kits', 'procedure_supply_usage',
    'instrument_sets', 'instrument_set_usage',
    'operating_theatres', 'theatre_bookings', 'theatre_block_times',
    'operating_theatre_bookings', 'otlt_sessions',
    'surgical_counts', 'surgical_waiting_list',
    'anesthesia_records', 'pacu_admissions',
    'external_procedure_referrals', 'who_safety_checklists',
    'post_op_care_records', 'post_op_order_sets',
    'drug_consumable_templates', 'consumable_usage',
  ],
  insurance: ['insurance_claims', 'insurance_providers', 'patient_insurance'],
  workforce: [
    'employee_profiles', 'attendance_records',
    'shift_templates', 'shift_assignments', 'shift_swap_requests',
    'roster_periods', 'leave_requests', 'leave_types',
    'allowance_types', 'allowance_grants',
    'compensation_profiles', 'payroll_periods', 'payroll_schedules',
    'payslips', 'performance_metrics', 'performance_reviews',
    'performance_snapshots', 'cme_events', 'cme_attendances',
    'site_visits', 'workforce_tips',
  ],
};

/** Reverse lookup built once: table name -> owning plan modules. */
const DATA_TABLE_OWNERS = {};
Object.entries(DATA_TABLE_MODULES).forEach(([modId, tables]) => {
  tables.forEach((t) => {
    (DATA_TABLE_OWNERS[t] = DATA_TABLE_OWNERS[t] || []).push(modId);
  });
});

/**
 * True when an exportable data table belongs to the tenant's plan.
 * @param {string} tableName - DB table name from the data-export endpoints
 * @param {Function} hasModuleAccess - SubscriptionContext.hasModuleAccess
 * @param {Object} subscription - tenant subscription (add-on grants)
 * @returns {boolean}
 */
export const isDataTableVisible = (tableName, hasModuleAccess, subscription) => {
  const owners = DATA_TABLE_OWNERS[tableName];
  if (!owners || owners.length === 0) return true; // shared platform data
  return owners.some((owner) => {
    if (hasModuleAccess(owner)) return true;
    // Section-unlocking add-ons (quick_reports, online_bookings) carry no
    // module grant — check the purchased add-on list directly, and treat
    // Enterprise's locked add-ons as granted without metadata.
    if ((subscription?.addOns || []).includes(owner)) return true;
    const plan = normalizedPlanName(subscription);
    return plan === 'Enterprise' && PREMIUM_LOCKED_ADDONS.includes(owner);
  });
};

export const LOCKED_TABS = {
  patients: ['followup', 'analytics', 'print_timeline'],
  // NOTE: 'order_sets' is intentionally NOT locked — the Order Sets tab and
  // the Order Set picker in the Rx tab are available on Standard/demo.
  clinical: ['followups', 'procedures', 'transfusion'],
  // NOTE: 'samples' is intentionally NOT locked — the specimen registry and
  // the collect → accept sample workflow are core lab functionality available
  // on Standard/demo (Feature.LAB_SAMPLES is in the Standard plan base set).
  laboratory: ['qc_management', 'analytics'],
  radiology: ['inventory', 'analytics'],
  // NOTE: 'drug_safety' is a feature-level lock (not a tab), but is included
  // here so isTabLocked('pharmacy', 'drug_safety', ...) returns the correct
  // lock state. The Pharmacy page uses it to gate the drug-safety button/menu.
  pharmacy: ['analytics', 'drug_safety'],
  billing: ['analytics'],
  finance: ['cash-flow', 'ar-ap', 'balance-sheet', 'ar-aging'],
  inventory: ['purchase-orders', 'supplier-analytics'],
  notifications: ['medications'],
};

/**
 * Check if a specific tab within a module is locked for the user's plan
 * @param {string} moduleId - The module identifier (e.g., 'laboratory')
 * @param {string} tabId - The tab identifier (e.g., 'analytics')
 * @param {Object} subscription - User's subscription object
 * @param {Object} user - User object with role information
 * @returns {boolean} - True if the tab is locked (should show PREMIUM badge)
 */
export const isTabLocked = (moduleId, tabId, subscription, user = null, isDemo = false) => {
  // Superusers and system developers bypass plan restrictions — UNLESS they
  // are simulating a different plan (subscription.extra_metadata.simulated)
  const isSimulating = subscription && subscription.extraMetadata && subscription.extraMetadata.simulated;
  if (!isSimulating && user && (user.role === 'SYSTEM_DEVELOPER' || user.is_superuser)) {
    return false;
  }

  // Normalize plan name to titlecase (e.g. 'standard' -> 'Standard')
  const plan = subscription && subscription.plan && subscription.status === 'active'
    ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1).toLowerCase()
    : null;

  // Entry tiers (Starter / Starter 2) — allowlist: every tab NOT listed for
  // the module is hidden entirely. Starter 2 additionally hides specific
  // tabs inside unrestricted modules (Patients > Analytics) and otherwise
  // keeps Standard-style locked badges on premium tabs.
  if (plan === 'Starter' || plan === 'Starter 2') {
    const allowed = (plan === 'Starter' ? STARTER_TABS : STARTER2_TABS)[moduleId];
    if (allowed) {
      return allowed.includes(tabId) ? false : 'hidden';
    }
    if (plan === 'Starter 2' && (STARTER2_HIDDEN_TABS[moduleId] || []).includes(tabId)) {
      return 'hidden';
    }
    if (plan === 'Starter 2' && (LOCKED_TABS[moduleId] || []).includes(tabId)) {
      return 'locked';
    }
    return false;
  }

  const lockedTabs = LOCKED_TABS[moduleId];
  if (!lockedTabs || !lockedTabs.includes(tabId)) {
    return false;
  }
  // Demo/trial users: hide locked tabs entirely
  if (isDemo) {
    return 'hidden';
  }
  // Premium plan users: show with PREMIUM badge (disabled)
  if (plan === 'Premium') {
    return 'locked';
  }
  if (plan) {
    // Enterprise: tab is accessible
    return false;
  }
  // No active subscription — fail closed: lock the tab rather than expose it
  return 'locked';
};

/**
 * Check if a module is accessible based on the user's subscription
 * @param {string} moduleId - The module identifier (e.g., 'emergency', 'ipd')
 * @param {Object} subscription - User's subscription object with plan and addOns
 * @param {Object} user - User object with role information
 * @returns {boolean} - True if the module is accessible
 */
export const canAccessModule = (moduleId, subscription, user = null) => {
  // Only system developers and superusers bypass subscription checks
  // — UNLESS they are simulating a different plan
  const isSimulating = subscription && subscription.extraMetadata && subscription.extraMetadata.simulated;
  if (!isSimulating && user && (user.role === 'SYSTEM_DEVELOPER' || user.is_superuser)) {
    return true;
  }

  // No subscription = no access (except for basic info pages)
  if (!subscription || !subscription.plan) {
    return false;
  }

  const { plan: rawPlan, status, addOns = [] } = subscription;

  // Subscription must be active
  if (status !== 'active') {
    return false;
  }

  // Normalize plan name to titlecase (e.g., 'standard' -> 'Standard')
  const plan = rawPlan ? rawPlan.charAt(0).toUpperCase() + rawPlan.slice(1).toLowerCase() : rawPlan;

  const planFeatures = PLAN_FEATURES[plan];

  // Invalid plan
  if (!planFeatures) {
    return false;
  }

  // Check if module is in the plan
  const hasModule = planFeatures.modules.includes(moduleId);

  // Enterprise module add-ons act as a grant subset: when the subscription
  // carries an explicit non-empty addOns list, untoggled modules lose
  // access. An empty/absent list means legacy Enterprise — everything
  // included. Locked add-ons (Printing, Clinic Chat, Quick Reports, Online
  // Bookings) are always auto-granted regardless.
  if (plan === 'Enterprise' && ENTERPRISE_MODULE_ADDONS.includes(moduleId)) {
    if (addOns.length === 0) return true;
    return hasModule && addOns.includes(moduleId);
  }
  if (plan === 'Enterprise') {
    for (const addOnId of PREMIUM_LOCKED_ADDONS) {
      const modules = ADDON_MODULES[addOnId] || [];
      if (modules.includes(moduleId)) {
        return true;
      }
    }
  }

  // For Premium plan, add-on modules are only accessible if purchased.
  // They are NOT in the Premium base modules, so we grant access when the
  // corresponding add-on is active.
  // ADDON_MODULES maps add-on IDs to the module(s) they unlock.
  if (plan === 'Premium') {
    for (const addOnId of STANDARD_ADDONS) {
      const modules = ADDON_MODULES[addOnId] || [];
      if (modules.includes(moduleId) && addOns.includes(addOnId)) {
        return true;
      }
    }
  }

  // Entry tiers sell a smaller add-on set — same metadata-driven grant.
  const entryTierAddons = plan === 'Starter' ? STARTER_SELLABLE_ADDONS
    : plan === 'Starter 2' ? STARTER2_SELLABLE_ADDONS : [];
  for (const addOnId of entryTierAddons) {
    const modules = ADDON_MODULES[addOnId] || [];
    if (modules.includes(moduleId) && addOns.includes(addOnId)) {
      return true;
    }
  }

  return hasModule;
};

/**
 * Get all accessible modules for a subscription
 * @param {Object} subscription - User's subscription object
 * @param {Object} user - User object with role information
 * @returns {string[]} - Array of accessible module IDs
 */
export const getAccessibleModules = (subscription, user = null) => {
  // System developers and superusers bypass subscription checks
  // — UNLESS they are simulating a different plan
  const isSimulating = subscription && subscription.extraMetadata && subscription.extraMetadata.simulated;
  if (!isSimulating && user && (user.role === 'SYSTEM_DEVELOPER' || user.is_superuser)) {
    return [
      'patients', 'clinical', 'emergency', 'ipd', 'outpatient_therapy',
      'chronic_care', 'maternal', 'imnci', 'laboratory', 'radiology',
      'procedures', 'pharmacy', 'billing', 'finance', 'reports',
      'family_planning', 'blood_bank', 'insurance', 'inventory',
      'workforce', 'chat', 'analytics', 'admin', 'print',
      'online_bookings', 'notifications',
      'superadmin'  // Include superadmin module for superusers
    ];
  }

  if (!subscription || !subscription.plan || subscription.status !== 'active') {
    return [];
  }

  const { plan: rawPlan, addOns = [] } = subscription;

  // Normalize plan name to titlecase (e.g., 'standard' -> 'Standard')
  const plan = rawPlan ? rawPlan.charAt(0).toUpperCase() + rawPlan.slice(1).toLowerCase() : rawPlan;

  const planFeatures = PLAN_FEATURES[plan];

  if (!planFeatures) {
    return [];
  }

  // For Premium, start with base modules then add any purchased add-on
  // modules that aren't in the base set.
  // ADDON_MODULES maps add-on IDs to the module(s) they unlock.
  if (plan === 'Premium') {
    const base = [...planFeatures.modules];
    for (const addOnId of addOns) {
      if (STANDARD_ADDONS.includes(addOnId)) {
        const modules = ADDON_MODULES[addOnId] || [];
        for (const mod of modules) {
          if (!base.includes(mod)) {
            base.push(mod);
          }
        }
      }
    }
    return base;
  }

  // Entry tiers: base modules plus any purchased add-on modules from their
  // sellable set.
  if (plan === 'Starter' || plan === 'Starter 2') {
    const sellable = plan === 'Starter' ? STARTER_SELLABLE_ADDONS : STARTER2_SELLABLE_ADDONS;
    const base = [...planFeatures.modules];
    for (const addOnId of addOns) {
      if (sellable.includes(addOnId)) {
        for (const mod of ADDON_MODULES[addOnId] || []) {
          if (!base.includes(mod)) {
            base.push(mod);
          }
        }
      }
    }
    return base;
  }

  // Enterprise: full module list, minus module add-ons the superadmin has
  // toggled off (an explicit non-empty addOns list is a grant subset;
  // empty/absent = legacy Enterprise — everything included).
  if (plan === 'Enterprise') {
    const base = planFeatures.modules.filter((moduleId) => {
      if (!ENTERPRISE_MODULE_ADDONS.includes(moduleId)) return true;
      if (addOns.length === 0) return true;
      return addOns.includes(moduleId);
    });
    // Locked add-on modules are always included
    for (const addOnId of PREMIUM_LOCKED_ADDONS) {
      const modules = ADDON_MODULES[addOnId] || [];
      for (const mod of modules) {
        if (!base.includes(mod)) {
          base.push(mod);
        }
      }
    }
    return base;
  }

  return planFeatures.modules;
};
